<!-- CareManagement_MyTasks.vue -->
<!--
  Cross-patient CARE GRAPH to-do panel. One row per open care-graph `task` node
  across all patients. A task is "open" when its parent signal (via the `causes`
  edge) is not resolved. Pure care graph — NOT the operational state machine.
  Data: GET /api/careplannodes/todo/. Clicking a task opens the Patient_Drawer
  to the Care Graph tab.
-->
<template>
  <div class="flex flex-col" style="height: calc(100vh - 165px);">
    <!-- Header: title + summary chips -->
    <div class="px-4 py-2.5 border-b border-gray-200 bg-white shrink-0">
      <div class="flex items-center justify-between">
        <div>
          <div class="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Care Graph To-Do</div>
          <!-- Stats skeleton -->
          <div v-if="loading" class="flex items-center gap-2">
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-16 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-24 bg-gray-200 rounded animate-pulse"></div>
            </div>
            <div class="px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <div class="h-5 w-20 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
          <!-- Stats loaded -->
          <div v-else class="flex items-center gap-2">
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-900">{{ counts.total }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Open</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-700">{{ counts.planned }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Planned</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-blue-50 border border-blue-200">
              <span class="text-base font-bold text-blue-700">{{ counts.attempted }}</span>
              <span class="text-[10px] uppercase tracking-wider text-blue-600">Attempted</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-gray-50 border border-gray-200">
              <span class="text-base font-bold text-gray-700">{{ counts.patients }}</span>
              <span class="text-[10px] uppercase tracking-wider text-gray-400">Patients</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200">
              <span class="text-base font-bold text-amber-700">{{ counts.needs_reply }}</span>
              <span class="text-[10px] uppercase tracking-wider text-amber-600">Needs a reply</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-primary-50 border border-primary-200">
              <span class="text-base font-bold text-primary-700">{{ counts.new }}</span>
              <span class="text-[10px] uppercase tracking-wider text-primary-600">New to you</span>
            </div>
            <div class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-50 border border-amber-200">
              <span class="text-base font-bold text-amber-700">{{ counts.overdue }}</span>
              <span class="text-[10px] uppercase tracking-wider text-amber-600">Overdue</span>
            </div>
          </div>
        </div>
        <button class="cm-icon-btn" title="Refresh" @click="loadTasks">
          <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :spin="loading" style="font-size: 12px;" />
        </button>
      </div>
    </div>

    <!-- Filter bar -->
    <div class="px-4 py-2 flex items-center flex-wrap gap-3 border-b border-gray-200 bg-gray-50 shrink-0">
      <!-- Search -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Search</label>
        <input
          v-model="searchQuery"
          type="text"
          placeholder="Patient, task, or signal..."
          class="w-56 px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      <!-- Ownership scope: My Patients (my caseload) vs My To-Dos (to-dos
           assigned to me). De-selectable — clicking the active chip turns it
           off, showing the whole tenant's to-dos (for coverage/management).
           Selecting a specific Care Manager (below) also clears the scope so
           you see that person's list unfiltered. -->
      <button
        :class="chipClass(scopeFilter === 'my_patients', 'gray')"
        @click="setScope('my_patients')"
      >My Patients</button>
      <button
        :class="chipClass(scopeFilter === 'my_todos', 'blue')"
        @click="setScope('my_todos')"
      >My To-Dos</button>

      <!-- Care Manager filter: view another person's (or everyone's) to-dos.
           Filters rows by owner_entity_id. Reuses the already-loaded members. -->
      <div>
        <label class="block text-xs font-medium text-gray-600 mb-1">Care Manager</label>
        <select
          v-model="ownerFilter"
          class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 max-w-[180px]"
          @change="onOwnerFilterChange"
        >
          <option :value="null">All care managers</option>
          <option value="unassigned">Unassigned</option>
          <option v-for="m in sortedMembers" :key="m.id" :value="m.id">{{ m.name }}</option>
        </select>
      </div>

      <!-- Status quick filters -->
      <button
        :class="chipClass(statusFilter === 'planned', 'gray')"
        @click="statusFilter = statusFilter === 'planned' ? null : 'planned'"
      >Planned</button>
      <button
        :class="chipClass(statusFilter === 'attempted', 'blue')"
        @click="statusFilter = statusFilter === 'attempted' ? null : 'attempted'"
      >Attempted</button>

      <span class="w-px h-6 bg-gray-300 mt-4"></span>

      <button
        :class="chipClass(lensFilter === 'needs_reply', 'amber')"
        @click="setLens('needs_reply')"
      >Needs a reply<span v-if="counts.needs_reply" class="ml-1.5 opacity-70">{{ counts.needs_reply }}</span></button>
      <button
        :class="chipClass(lensFilter === 'new', 'blue')"
        @click="setLens('new')"
      >New to you<span v-if="counts.new" class="ml-1.5 opacity-70">{{ counts.new }}</span></button>
      <button
        :class="chipClass(lensFilter === 'overdue', 'amber')"
        @click="setLens('overdue')"
      >Overdue<span v-if="counts.overdue" class="ml-1.5 opacity-70">{{ counts.overdue }}</span></button>

      <div class="ml-auto flex items-center gap-2">
        <ExportMenu :disabled="!rows.length" :exports="[{ type: 'csv', action: exportCsv }]" />
      </div>
    </div>

    <!-- Main content: task list (left) + drawer (right) -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Task list -->
      <div
        :style="{ width: selectedPatient ? splitPct + '%' : '100%' }"
        class="flex flex-col border-r border-gray-200 overflow-y-auto overflow-x-hidden min-w-0"
      >
        <table class="w-full text-sm">
          <thead class="sticky top-0 bg-gray-50 z-10 border-b border-gray-200">
            <tr class="text-left text-[11px] uppercase tracking-wider text-gray-500">
              <th class="px-4 py-2 font-semibold w-24">Status</th>
              <th class="px-3 py-2 font-semibold cursor-pointer w-32" @click="setSort('patient')">
                Patient <SortCaret :active="sortKey === 'patient'" :dir="sortDir" />
              </th>
              <th class="px-3 py-2 font-semibold w-[24%]">Signal</th>
              <th class="px-3 py-2 font-semibold">Task</th>
              <th class="px-3 py-2 font-semibold w-40">Assigned</th>
              <th class="px-3 py-2 font-semibold w-16 text-center">Notes</th>
            </tr>
          </thead>
          <tbody>
            <!-- Loading skeleton -->
            <template v-if="loading">
              <tr v-for="n in 12" :key="'sk' + n" class="border-b border-gray-100">
                <td v-for="c in 6" :key="c" class="px-4 py-2.5">
                  <div class="h-3 bg-gray-100 rounded animate-pulse" :style="{ width: (40 + (c * 9) % 50) + '%' }"></div>
                </td>
              </tr>
            </template>

            <!-- Empty state -->
            <tr v-else-if="!filteredTasks.length">
              <td colspan="6" class="px-4 py-16 text-center text-gray-400">
                <FontAwesomeIcon :icon="['fas', 'circle-check']" class="text-2xl mb-2 text-green-300" />
                <div class="text-sm">{{ emptyStateText }}</div>
              </td>
            </tr>

            <!-- Task rows, grouped by patient when sorted by patient -->
            <template v-else>
              <template v-for="item in tableRows" :key="item.key">
                <tr v-if="item.type === 'patient'" class="bg-gray-50 border-b border-gray-200">
                  <td colspan="6" class="px-4 py-1.5">
                    <span class="text-xs font-semibold text-gray-800 sensitive">{{ titleCase(item.name) }}</span>
                    <span class="text-xs text-gray-400 ml-2 sensitive">{{ item.mbi || '' }}</span>
                    <span class="text-[11px] text-gray-400 ml-2">{{ item.count }} {{ item.count === 1 ? 'to-do' : 'to-dos' }}</span>
                  </td>
                </tr>
                <tr
                  v-else
                  :class="[
                    'border-b border-gray-100 cursor-pointer transition-colors',
                    selectedTaskId === item.row.task_id ? 'bg-primary-50' : 'hover:bg-gray-50',
                    item.row.is_new ? 'border-l-2 border-l-primary-500' : ''
                  ]"
                  @click="openTask(item.row)"
                >
                  <!-- Status and due -->
                  <td class="px-4 py-2.5 whitespace-nowrap align-top">
                    <span
                      class="inline-flex items-center gap-1.5 text-xs"
                      :class="item.row.task_status === 'attempted' ? 'text-blue-700' : 'text-gray-500'"
                    >
                      <span
                        class="w-1.5 h-1.5 rounded-full"
                        :class="item.row.task_status === 'attempted' ? 'bg-blue-500' : 'border border-gray-400'"
                      ></span>
                      {{ item.row.task_status === 'attempted' ? 'Attempted' : 'Planned' }}
                    </span>
                    <div v-if="item.row.due_date" class="text-[11px] mt-0.5" :class="item.row.is_overdue ? 'text-amber-700 font-medium' : 'text-gray-400'">
                      {{ item.row.is_overdue ? 'Overdue, due' : 'Due' }} {{ shortDate(item.row.due_date) }}
                    </div>
                  </td>
                  <!-- Patient -->
                  <td class="px-3 py-2.5 w-32 max-w-[128px] align-top">
                    <template v-if="!grouped">
                      <div class="text-xs text-gray-900 truncate sensitive" :title="item.row.patient_name">{{ titleCase(item.row.patient_name) }}</div>
                      <div class="text-xs text-gray-400 sensitive">{{ item.row.patient_identifier || '—' }}</div>
                    </template>
                  </td>
                  <!-- Signal -->
                  <td class="px-3 py-2.5 w-[28%] align-top">
                    <span v-if="item.row.signal_title" class="flex items-start gap-1.5 text-xs">
                      <span class="w-1.5 h-1.5 rounded-full shrink-0 mt-1" :class="signalDotClass(item.row.signal_status)"></span>
                      <span class="text-gray-700 leading-snug" :title="item.row.signal_title" data-demo-blur>{{ item.row.signal_title }}</span>
                    </span>
                    <span v-else-if="item.row.metric_name" class="text-xs text-gray-400">{{ item.row.metric_name }}</span>
                    <span v-else class="text-xs text-gray-400">—</span>
                  </td>
                  <!-- Task -->
                  <td class="px-3 py-2.5 align-top">
                    <div class="flex items-center gap-1.5 flex-wrap">
                      <span class="text-xs font-semibold text-gray-900 leading-snug" :title="item.row.task_title" data-demo-blur>{{ item.row.task_title || '—' }}</span>
                      <span v-if="sourceTag(item.row)" class="px-1.5 py-0.5 rounded text-[10px] font-medium bg-primary-50 text-primary-700 whitespace-nowrap">{{ sourceTag(item.row) }}</span>
                    </div>
                    <div v-if="item.row.task_summary" class="text-xs text-gray-600 leading-snug mt-1" data-demo-blur>{{ item.row.task_summary }}</div>
                  </td>
                  <!-- Assigned (inline reassign) -->
                  <td class="px-3 py-2.5 align-top" @click.stop>
                    <CareGraphAssignee
                      :owner-entity-id="item.row.owner_entity_id"
                      :owner-name="item.row.owner_name"
                      :members="members"
                      @assign="mid => onReassign(item.row, mid)"
                      @unassign="onReassign(item.row, null)"
                    />
                  </td>
                  <!-- Notes (comment thread) -->
                  <td class="px-3 py-2.5 align-top text-center" @click.stop>
                    <button
                      class="inline-flex items-center gap-1 text-xs rounded px-1.5 py-0.5 hover:bg-gray-100"
                      :class="item.row.comment_count && !item.row.last_comment_is_mine ? 'text-amber-700' : 'text-gray-400'"
                      :title="item.row.comment_count ? `${item.row.comment_count} notes` : 'Add a note'"
                      @click="toggleThread(item.row)"
                    >
                      <FontAwesomeIcon :icon="['far', 'comment']" style="font-size: 12px;" />
                      <span v-if="item.row.comment_count">{{ item.row.comment_count }}</span>
                    </button>
                  </td>
                </tr>
                <tr v-if="item.type === 'row' && threads[item.row.task_id]?.open" class="border-b border-gray-100 bg-gray-50/60">
                  <td colspan="6" class="px-4 py-2.5" @click.stop>
                    <div class="ml-8 max-w-2xl space-y-2">
                      <div v-if="threads[item.row.task_id].loading" class="text-xs text-gray-400">Loading notes…</div>
                      <div
                        v-for="c in threads[item.row.task_id].comments"
                        :key="c.id"
                        class="flex items-start gap-2 text-xs"
                      >
                        <span class="inline-flex items-center justify-center w-5 h-5 rounded-full text-[9px] font-semibold text-white bg-gray-400 shrink-0 mt-0.5">{{ initials(c.author_name) }}</span>
                        <div class="min-w-0">
                          <span class="text-gray-500 mr-1.5 sensitive">{{ c.is_mine ? 'You' : c.author_name }}, {{ relTime(c.created_at) }}</span>
                          <span class="text-gray-800" data-demo-blur>{{ c.text }}</span>
                        </div>
                      </div>
                      <div class="flex items-center gap-2">
                        <input
                          v-model="threads[item.row.task_id].draft"
                          type="text"
                          :placeholder="replyPlaceholder(item.row)"
                          class="flex-1 px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          @keyup.enter="sendReply(item.row)"
                        />
                        <button
                          class="px-3 py-1.5 text-xs font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                          :disabled="!(threads[item.row.task_id].draft || '').trim() || threads[item.row.task_id].sending"
                          @click="sendReply(item.row)"
                        >Send</button>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </template>
          </tbody>
        </table>
        <div
          v-if="!loading && total > pageSize"
          class="sticky bottom-0 flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-white text-xs text-gray-500"
        >
          <span>{{ pageStart }}–{{ pageEnd }} of {{ total }}</span>
          <div class="flex items-center gap-2">
            <button class="cm-icon-btn" :disabled="page <= 1" title="Previous page" @click="goToPage(page - 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-left']" style="font-size: 11px;" />
            </button>
            <span>Page {{ page }} of {{ pageCount }}</span>
            <button class="cm-icon-btn" :disabled="!hasMore" title="Next page" @click="goToPage(page + 1)">
              <FontAwesomeIcon :icon="['fas', 'chevron-right']" style="font-size: 11px;" />
            </button>
          </div>
        </div>
      </div>

      <!-- Drag handle -->
      <div
        v-if="selectedPatient"
        class="w-1 cursor-col-resize bg-gray-200 hover:bg-primary-400 active:bg-primary-500 transition-colors shrink-0"
        @mousedown="startDrag"
      ></div>

      <!-- Drawer -->
      <div
        v-if="selectedPatient"
        :style="{ width: (100 - splitPct) + '%' }"
        class="flex flex-col overflow-hidden"
      >
        <div class="flex-1 overflow-hidden">
          <Patient_Drawer
            ref="patientDrawer"
            :patient-identifier="selectedPatient.patient_identifier"
            :entity-id="selectedPatient.entity_id"
            :patient-data="selectedPatient"
            :patient-name="selectedPatient.patient_name"
            :enabled-tabs="drawerEnabledTabs"
            :show-header="true"
            :default-tab="'care_plan'"
            metric-category="Care Management"
            @show-message="showMessage"
            @task-updated="loadTasks"
            @close="closeDrawer"
          />
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition
      enter-active-class="transition-opacity duration-200"
      leave-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="message"
        :class="[
          'fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 max-w-md',
          message.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        ]"
      >
        <div class="flex items-start">
          <FontAwesomeIcon
            :icon="['fas', message.type === 'success' ? 'check-circle' : 'exclamation-circle']"
            class="mr-2 mt-0.5"
          />
          <div class="font-medium">{{ message.text }}</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { h } from 'vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import Patient_Drawer from '@/components/Patient_Drawer/Patient_Drawer.vue';
import CareGraphAssignee from '@/components/Care_Management/CareGraphAssignee.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';

// Tiny inline sort-caret (Vue 3 render function, no template compiler needed).
const SortCaret = {
  name: 'SortCaret',
  props: { active: Boolean, dir: { type: String, default: 'asc' } },
  setup(props) {
    return () => props.active
      ? h('span', { class: 'ml-0.5 text-gray-400' }, props.dir === 'asc' ? '\u25B2' : '\u25BC')
      : null;
  },
};

export default {
  name: 'CareManagementMyTasks',
  components: { FontAwesomeIcon, Patient_Drawer, SortCaret, CareGraphAssignee, ExportMenu },
  data() {
    return {
      loading: false,
      rows: [],
      page: 1,
      pageSize: 100,
      total: 0,
      hasMore: false,
      serverCounts: null,
      newSince: null,
      threads: {},

      searchQuery: '',
      statusFilter: null,
      lensFilter: null,
      // Ownership lens. 'my_patients' = to-dos for patients I'm the care
      // manager for (is_my_patient); 'my_todos' = to-dos assigned to me
      // (is_my_todo); null = no personal lens (whole tenant / another CM).
      // Defaults to My Patients per care-advisor request.
      scopeFilter: 'my_patients',
      // Care Manager filter: null = all, 'unassigned' = no owner, or a
      // member id (== owner_entity_id) to view that person's to-dos.
      ownerFilter: null,

      sortKey: 'patient',
      sortDir: 'asc',

      selectedPatient: null,
      selectedTaskId: null,
      splitPct: parseInt(localStorage.getItem('cm_split_pct') || '50'),

      members: [],
      message: null,
    };
  },
  computed: {
    ...mapGetters('tenant', ['current_tenant_slug']),
    drawerEnabledTabs() {
      const base = ['details', 'utilization', 'programs', 'engagement', 'care_plan', 'care_graph', 'care_team'];
      if (this.current_tenant_slug === 'ihci') {
        return [...base.filter(t => t !== 'care_plan'), 'insights'];
      }
      return base;
    },
    sortedMembers() {
      return [...this.members].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    },
    filteredTasks() {
      return this.rows;
    },
    grouped() {
      return this.sortKey === 'patient';
    },
    tableRows() {
      if (!this.grouped) return this.rows.map(r => ({ type: 'row', key: r.task_id, row: r }));
      const out = [];
      let last = null;
      for (const r of this.rows) {
        if (r.entity_id !== last) {
          const count = this.rows.filter(x => x.entity_id === r.entity_id).length;
          out.push({ type: 'patient', key: `p-${r.entity_id}`, name: r.patient_name, mbi: r.patient_identifier, count });
          last = r.entity_id;
        }
        out.push({ type: 'row', key: r.task_id, row: r });
      }
      return out;
    },
    queryParams() {
      const params = { sort: this.sortKey, dir: this.sortDir };
      if (this.scopeFilter) params.scope = this.scopeFilter;
      if (this.ownerFilter != null) params.owner = String(this.ownerFilter);
      if (this.statusFilter) params.status = this.statusFilter;
      if (this.lensFilter) params.lens = this.lensFilter;
      if (this.searchQuery.trim()) params.q = this.searchQuery.trim();
      if (this.newSince) params.new_since = this.newSince;
      return params;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.total / this.pageSize));
    },
    pageStart() {
      return this.total ? (this.page - 1) * this.pageSize + 1 : 0;
    },
    pageEnd() {
      return Math.min(this.total, this.page * this.pageSize);
    },
    emptyStateText() {
      if (this.lensFilter === 'needs_reply') return 'Nothing is waiting on a reply from you';
      if (this.lensFilter === 'new') return 'Nothing new since your last visit';
      if (this.lensFilter === 'overdue') return 'No overdue to-dos';
      if (this.ownerFilter === 'unassigned') return 'No unassigned care-graph to-dos';
      if (this.ownerFilter != null) {
        const m = this.members.find(x => String(x.id) === String(this.ownerFilter));
        return `No open care-graph to-dos for ${m ? m.name : 'that care manager'}`;
      }
      if (this.scopeFilter === 'my_patients') return 'No open care-graph tasks for your patients';
      if (this.scopeFilter === 'my_todos') return 'No care-graph to-dos assigned to you';
      return 'No open care-graph tasks';
    },
    counts() {
      return this.serverCounts || { total: 0, planned: 0, attempted: 0, patients: 0, needs_reply: 0, new: 0, overdue: 0 };
    },
  },
  watch: {
    scopeFilter() { this.reloadFromStart(); },
    ownerFilter() { this.reloadFromStart(); },
    statusFilter() { this.reloadFromStart(); },
    lensFilter() { this.reloadFromStart(); },
    sortKey() { this.reloadFromStart(); },
    sortDir() { this.reloadFromStart(); },
    searchQuery() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.reloadFromStart(), 300);
    },
  },
  mounted() {
    this.loadTasks();
    this.loadMembers();
    this._onMessage = (event) => {
      if (event.data?.type === 'clinical-session-saved') this.loadTasks();
    };
    window.addEventListener('message', this._onMessage);
  },
  beforeUnmount() {
    window.removeEventListener('message', this._onMessage);
    clearTimeout(this._searchTimer);
  },
  methods: {
    chipClass(active, color) {
      const on = {
        blue: 'bg-blue-500 text-white border-blue-500',
        gray: 'bg-gray-600 text-white border-gray-600',
        amber: 'bg-amber-600 text-white border-amber-600',
      }[color];
      return [
        'px-3 py-1.5 text-xs font-medium rounded-full border transition-colors whitespace-nowrap mt-4',
        active ? on : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
      ];
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = key;
        this.sortDir = 'asc';
      }
    },
    setScope(scope) {
      // De-selectable: clicking the active chip clears the lens (show all).
      this.scopeFilter = this.scopeFilter === scope ? null : scope;
    },
    setLens(lens) {
      this.lensFilter = this.lensFilter === lens ? null : lens;
    },
    sourceTag(row) {
      const s = row.source;
      if (!s) return '';
      if (s.kind === 'transition') return `from ${s.from_metric_name || 'another program'}`;
      if (s.kind === 'closure') return `${s.from_metric_name || 'program'} closed`;
      if (s.kind === 'request') return s.author ? `asked by ${s.author}` : 'request';
      return '';
    },
    shortDate(v) {
      const m = String(v || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (!m) return v || '';
      const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${MON[Number(m[2]) - 1]} ${Number(m[3])}`;
    },
    relTime(iso) {
      if (!iso) return '';
      const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
      if (mins < 1) return 'just now';
      if (mins < 60) return `${mins}m ago`;
      const hrs = Math.round(mins / 60);
      if (hrs < 24) return `${hrs}h ago`;
      const days = Math.round(hrs / 24);
      return days === 1 ? 'yesterday' : `${days}d ago`;
    },
    initials(name) {
      return (name || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';
    },
    replyPlaceholder(row) {
      const s = row.source;
      if (s && s.author && s.kind !== 'closure') return `Reply to ${s.author}`;
      return 'Add a note for whoever picks this up';
    },
    async toggleThread(row) {
      const id = row.task_id;
      const cur = this.threads[id];
      if (cur && cur.open) {
        this.threads = { ...this.threads, [id]: { ...cur, open: false } };
        return;
      }
      this.threads = { ...this.threads, [id]: { open: true, loading: true, comments: [], draft: '', sending: false } };
      try {
        const { data } = await this.$axios.get(`/api/careplannodes/${id}/comments/`);
        this.threads = { ...this.threads, [id]: { ...this.threads[id], loading: false, comments: data?.comments || [] } };
      } catch {
        this.threads = { ...this.threads, [id]: { ...this.threads[id], loading: false } };
        this.showMessage('Could not load notes', 'error');
      }
    },
    async sendReply(row) {
      const id = row.task_id;
      const t = this.threads[id];
      const text = (t?.draft || '').trim();
      if (!text || t.sending) return;
      this.threads = { ...this.threads, [id]: { ...t, sending: true } };
      try {
        const { data } = await this.$axios.post(`/api/careplannodes/${id}/comments/`, { text });
        this.threads = { ...this.threads, [id]: { ...this.threads[id], sending: false, draft: '', comments: data?.comments || [] } };
        row.comment_count = (data?.comments || []).length;
        row.last_comment_is_mine = true;
      } catch {
        this.threads = { ...this.threads, [id]: { ...this.threads[id], sending: false } };
        this.showMessage('Could not send note', 'error');
      }
    },
    onOwnerFilterChange() {
      // Picking a specific care manager (or Unassigned) is a cross-CM view, so
      // drop the personal lens — otherwise "My Patients" ∩ "Angie" hides rows
      // unexpectedly. "All care managers" leaves the personal lens as-is.
      if (this.ownerFilter != null) this.scopeFilter = null;
    },
    signalDotClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'open') return 'bg-amber-500';
      if (s === 'not_addressed') return 'bg-gray-300';
      return 'bg-gray-300';
    },
    titleCase(str) {
      if (!str) return '';
      return str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
    },
    reloadFromStart() {
      this.page = 1;
      this.loadTasks();
    },
    goToPage(n) {
      this.page = Math.max(1, n);
      this.loadTasks();
    },
    async loadTasks() {
      const seq = (this._reqSeq = (this._reqSeq || 0) + 1);
      this.loading = true;
      try {
        const params = { ...this.queryParams, page: this.page, page_size: this.pageSize };
        if (!this.newSince) params.mark_seen = 1;
        const { data } = await this.$axios.get('/api/careplannodes/todo/', { params });
        if (seq !== this._reqSeq) return;
        if (data?.success) {
          this.rows = data.rows || [];
          this.total = data.total || 0;
          this.hasMore = !!data.has_more;
          this.serverCounts = data.counts || null;
          if (data.new_since) this.newSince = data.new_since;
        }
      } catch (e) {
        if (seq !== this._reqSeq) return;
        console.error('Error loading care-graph tasks:', e);
      } finally {
        if (seq === this._reqSeq) this.loading = false;
      }
    },
    async loadMembers() {
      // Tenant's care-team members (tenant-scoped server-side; no hardcoded type).
      try {
        const { data } = await this.$axios.get('/api/careplannodes/addressees/');
        this.members = (data?.members || []).map(r => ({ id: r.id, name: r.name || 'Unknown', kind: r.kind }));
      } catch (e) {
        console.error('Error loading members:', e);
      }
    },
    async onReassign(row, memberId) {
      // memberId null -> unassign. Optimistic: update the row, then persist.
      const prevId = row.owner_entity_id;
      const prevName = row.owner_name;
      const m = this.members.find(x => String(x.id) === String(memberId));
      row.owner_entity_id = memberId ? Number(memberId) : null;
      row.owner_name = m ? m.name : null;
      try {
        if (memberId) {
          await this.$axios.post(`/api/careplannodes/${row.task_id}/assign/`, { member_id: Number(memberId) });
        } else {
          await this.$axios.post(`/api/careplannodes/${row.task_id}/unassign/`);
        }
      } catch {
        row.owner_entity_id = prevId;  // revert on failure
        row.owner_name = prevName;
        this.showMessage('Reassignment failed', 'error');
      }
    },
    openTask(row) {
      this.selectedTaskId = row.task_id;
      this.selectedPatient = {
        entity_id: row.entity_id,
        patient_identifier: row.patient_identifier,
        patient_name: row.patient_name,
      };
    },
    closeDrawer() {
      this.selectedPatient = null;
      this.selectedTaskId = null;
    },
    startDrag(e) {
      e.preventDefault();
      const container = e.target.parentElement;
      const onMove = (ev) => {
        const rect = container.getBoundingClientRect();
        let pct = ((ev.clientX - rect.left) / rect.width) * 100;
        pct = Math.max(25, Math.min(75, pct));
        this.splitPct = Math.round(pct);
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('cm_split_pct', String(this.splitPct));
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    async exportCsv() {
      let rows = [];
      try {
        const { data } = await this.$axios.get('/api/careplannodes/todo/', { params: this.queryParams });
        rows = data?.rows || [];
      } catch {
        this.showMessage('Export failed', 'error');
        return;
      }
      if (!rows.length) return;
      const esc = (v) => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? '"' + s.replace(/"/g, '""') + '"' : s;
      };
      const headers = ['Status', 'Patient', 'MBI', 'Task', 'Task Detail', 'Addresses Signal', 'Signal Status', 'Assigned To'];
      const csvRows = rows.map(r => [
        r.task_status || 'planned',
        r.patient_name,
        r.patient_identifier || '',
        r.task_title,
        r.task_summary || '',
        r.signal_title,
        r.signal_status,
        r.owner_name || '',
      ].map(esc).join(','));
      const csv = headers.join(',') + '\n' + csvRows.join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cm_care_graph_todo_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    showMessage(text, type) {
      this.message = { text, type };
      setTimeout(() => { this.message = null; }, 5000);
    },
  },
};
</script>
